import * as React from 'react';
import MuiToggleButton from '@mui/material/ToggleButton';
import { Link, useLocation } from 'react-router-dom';
import { styled, SxProps } from '@mui/material/styles';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import StyActionContainer from 'ui/StyledComponents/StyActionContainer';
import Flex from 'ui/Flex/Flex';
import { useHasTabBeenVisited } from 'components/SharedComponents/AuxiliaryTabs/hooks';

const StyTabs = styled(Box, { name: 'StyTabs' })(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    textAlign: 'center',
    padding: '1rem 0',
    '& .MuiTabs-indicator': {
        backgroundColor: 'transparent',
    },
    indicator: 'transparent',
    gap: theme.spacing(1),
}));

const StyLabel = styled(Typography, {
    name: 'StyLabel',
})({
    fontSize: '0.9em',
    display: 'flex',
    variant: 'body1SemiBold',
});
const StyToggleButton = styled(MuiToggleButton, { name: 'StyToggleButton' })(
    ({ theme }) => ({
        borderRadius: '32px',
        color: theme.palette.black,
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: 'inherit',
        },
        backgroundColor: theme.palette.background.default,
        textTransform: 'inherit', // remove uppercase on all buttons
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
        },
        '&.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.main,
        },
        '&.Mui-disabled': {
            color: theme.palette.lightGrey,
        },
    }),
);

const StyToggleButtonWithLink = styled(StyToggleButton, {
    name: 'StyToggleButtonWithLink',
})<
    React.ComponentProps<typeof Link> &
        React.ComponentProps<typeof MuiToggleButton>
>({
    textDecoration: 'none',
    '&:focus': {
        outline: 'none',
    },
    display: 'inline-flex',
    alignItems: 'center',
});

export type TabT = {
    label: string;
    key: string | number;
    children: React.ReactNode;
    pathname: string;
    disabled?: boolean;
    tabActions?: React.ReactNode;
};

// if keepSearch set to False:
//    - click on tabs will reset the search to empty string
//    - search will not be part of the front-end cache (children will not be remounted on change to search)
type Props = {
    tabsList: TabT[];
    className?: string;
    onTabClicked?: (arg1: TabT) => void;
    keepSearch?: boolean;
    shouldRedirect?: boolean;
    sx?: SxProps;
};

function AuxiliaryTabs({
    tabsList,
    className = '',
    onTabClicked = () => {},
    keepSearch = true,
    shouldRedirect = true,
    sx = {},
}: Props): React.ReactElement {
    const location = useLocation();
    const [value, setValue] = React.useState(tabsList[0].label);
    const isTabVisible = React.useCallback(
        (tab: TabT) =>
            shouldRedirect
                ? location.pathname === tab.pathname
                : tab.label === value,
        [location.pathname, value, shouldRedirect],
    );

    const hasTabBeenVisited = useHasTabBeenVisited(tabsList, keepSearch);

    const onClick = (tab: TabT) => {
        if (onTabClicked && !isTabVisible(tab)) {
            onTabClicked(tab);
        }
    };

    return (
        <Box className={className} sx={sx}>
            <Flex id="auxiliary-tab-header" alignItems="center">
                <StyTabs>
                    {tabsList.map((tab) => (
                        <React.Fragment key={tab.key}>
                            {shouldRedirect ? (
                                <StyToggleButtonWithLink
                                    to={{
                                        pathname: tab.pathname,
                                        search: keepSearch
                                            ? location.search
                                            : '',
                                    }}
                                    state={location.state}
                                    disabled={tab.disabled}
                                    key={tab.label}
                                    value={tab.pathname}
                                    disableRipple
                                    selected={isTabVisible(tab)}
                                    LinkComponent={Link}
                                    onClick={() => onClick(tab)}
                                >
                                    <StyLabel>{tab.label}</StyLabel>
                                </StyToggleButtonWithLink>
                            ) : (
                                <StyToggleButton
                                    key={tab.label}
                                    selected={isTabVisible(tab)}
                                    value={value}
                                    onClick={() => {
                                        setValue(tab.label);
                                    }}
                                >
                                    <StyLabel>{tab.label}</StyLabel>
                                </StyToggleButton>
                            )}
                        </React.Fragment>
                    ))}
                </StyTabs>
                <StyActionContainer>
                    {tabsList.find(isTabVisible)?.tabActions}
                </StyActionContainer>
            </Flex>
            {tabsList.map((tab) => (
                <Box
                    key={shouldRedirect ? tab.pathname : tab.label}
                    hidden={!isTabVisible(tab)}
                >
                    {shouldRedirect ? (
                        <>
                            {/* NOTE optimization: if the tab has already been visited, let's keep it in the DOM
                     in order to not unmount it */}

                            {hasTabBeenVisited[
                                tab.pathname +
                                    (keepSearch ? location.search : '')
                            ] && tab.children}
                        </>
                    ) : (
                        <>{tab.children}</>
                    )}
                </Box>
            ))}
        </Box>
    );
}
export default AuxiliaryTabs;
